import React, { useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import { styled } from '@this/src/components/constants/themes';
import type Hotel from '@this/src/domain/hotel/hotel';
import type ReserveInfo from '@this/src/domain/reserve_info';
import { ButtonBase } from '@this/shared/atoms/button';
import type { TripDuplicationInterface } from '../../reserve_confirm';
import TripDuplicationSection from '../input_customer_information/trip_duplication_section/trip_duplication_section';
import RestrictedHotelSection from '../input_customer_information/restricted_hotel_section/restricted_hotel_section';

interface Props {
  open: boolean;
  tripDuplications: TripDuplicationInterface[];
  restrictedHotels: Hotel[];
  reserveInfo: ReserveInfo;
  onClose: () => void;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const ConfirmCustomerConfirmModal: React.FC<Props> = ({
  open,
  tripDuplications,
  restrictedHotels,
  reserveInfo,
  onClose,
  onSubmit
}) => {
  const [tripDuplicationAgree, setTripDuplicationAgree] = useState(false);
  const [restrictedHotelAgree, setRestrictedHotelAgree] = useState(false);

  const groupedRestrictedHotels = useMemo(() => {
    return Object.values(
      restrictedHotels.reduce((acc, hotel) => {
        const key = hotel.name ?? '';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(hotel);
        return acc;
      }, {} as Record<string, Hotel[]>)
    );
  }, [restrictedHotels]);

  const tripDuplicationConfirm = useMemo(() => {
    return tripDuplications.length > 0;
  }, [tripDuplications]);

  const restrictedHotelConfirm = useMemo(() => {
    return restrictedHotels.length > 0;
  }, [restrictedHotels]);

  const handleTripDuplicationAgree = useCallback(() => {
    setTripDuplicationAgree(agree => !agree);
  }, []);

  const handleRestrictedHotelAgree = useCallback(() => {
    const agree = !restrictedHotelAgree;
    reserveInfo.setUserChecker(agree);
    setRestrictedHotelAgree(agree);
  }, [restrictedHotelAgree]);

  const agree = useMemo(() => {
    if (tripDuplicationConfirm && !tripDuplicationAgree) {
      return false;
    }

    if (restrictedHotelConfirm && !restrictedHotelAgree) {
      return false;
    }

    return true;
  }, [tripDuplicationAgree, restrictedHotelAgree, tripDuplicationConfirm, restrictedHotelConfirm]);

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onSubmit(e);
      onClose();
    },
    [onClose, onSubmit]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader>
        <HeaderContent>依頼前に確認ください</HeaderContent>
      </ModalHeader>
      <ModalBody>
        <Content>
          <p>以下の旅程を確認し、問題なければ手配を依頼してください。</p>
          <ConfirmContents>
            {tripDuplicationConfirm && (
              <ConfirmContent>
                <label>
                  <input
                    type="checkbox"
                    className="trip_duplication_agree"
                    checked={tripDuplicationAgree}
                    onChange={handleTripDuplicationAgree}
                  />
                  旅程の重複を確認しました
                </label>
                <div>
                  {tripDuplications.map((tripDuplication, index) => (
                    <TripDuplicationSection key={index} tripDuplication={tripDuplication} />
                  ))}
                </div>
              </ConfirmContent>
            )}
            {restrictedHotelConfirm && (
              <ConfirmContent>
                <label>
                  <input
                    type="checkbox"
                    className="restricted_hotel_agree"
                    checked={restrictedHotelAgree}
                    onChange={handleRestrictedHotelAgree}
                  />
                  宿泊時の条件を確認しました
                </label>
                <div>
                  {groupedRestrictedHotels.map((hotelGroup, index) => (
                    <RestrictedHotelSection key={index} restrictedHotels={hotelGroup} />
                  ))}
                </div>
              </ConfirmContent>
            )}
          </ConfirmContents>
        </Content>
      </ModalBody>
      <ModalFooter>
        <ConfirmButton type="button" role="confirm" onClick={handleClick} disabled={!agree}>
          手配を依頼する
        </ConfirmButton>
      </ModalFooter>
    </Modal>
  );
};

const HeaderContent = styled.div`
  padding: 5px 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 0;
`;

const ConfirmButton = styled.button`
  ${ButtonBase}
  margin-left: 15px;
  padding: 0.75em 1em;
  line-height: 1;
`;

const ConfirmContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConfirmContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
